import { useContext, useState } from "react";

import { useSearchParams } from "react-router-dom";
import { Box, IconButton, Button, Typography } from "@mui/material";
import useMediaQuery from "@mui/material/useMediaQuery";

import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";

import {
  UserPreset,
  CurPreset,
  UserTypes,
  TypesData,
  SofaPreset,
  SofaType,
} from "./DataTypes";
import { Trans } from "../Translations";
import { client } from "../RpcClient";
import { config } from "../Config";
import { vB } from "../Style";
import AdminRender from "../AdminRender";
import AdminPricesSync from "./AdminPricesSync";
import { Space } from "../DataTypes";

function iconWrapperCss(largerThen750: boolean) {
  return {
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxSizing: "border-box",
    padding: "12px 16px",
    [vB.xs]: {
      height: "140px",
    },
    [vB.lg]: {
      height: largerThen750 ? "170px" : "140px",
    },
    "@media (max-width:900px)": {
      height: "120px",
    },
    "@media (max-width:600px)": {
      height: "100px",
    },
  };
}

function iconImgCss() {
  return {
    pointerEvents: "none",
    maxWidth: "100%",
    maxHeight: "100%",
    width: "100%",
    height: "100%",
    objectFit: "contain",
    display: "block",
  };
}

function PresetChoice(props: {
  className: string;
  selected: boolean;
  preset: SofaPreset;
  onClick: () => void;
  isPopup?: boolean;
}) {
  const { dynTr } = useContext(Trans);
  const largerThen750 = useMediaQuery("(min-width:750px)");
  return (
    <Button
      className={props.className}
      sx={{
        marginRight: props.isPopup ? 0 : "10px",
        width: "100%",
        minWidth: 0,
        padding: 0,
        lineHeight: "1.1em",
        opacity: 1.0,
        flexDirection: "column",
        alignItems: "center",
        border: "1px solid #d9d9d9",
        borderRadius: "8px",
        transition: "all 120ms linear",
        ":hover": {
          backgroundColor: "transparent",
          borderColor: "#bdbdbd",
          boxShadow: "0 4px 10px rgba(0,0,0,0.14)",
        },
      }}
      onClick={props.onClick}
    >
      {props.preset.icon && (
        <Box
          sx={iconWrapperCss(largerThen750)}
        >
          <Box
            component="img"
            onDragStart={(e) => e.preventDefault()}
            src={`${config.apiUrl}/assets/${props.preset.icon}`}
            sx={iconImgCss()}
          />
        </Box>
      )}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          marginTop: "8px",
        }}
      >
        <Typography
          component="span"
          sx={{
            fontFamily: '"Montserrat", sans-serif',
            textTransform: "none",
            fontWeight: "bold",
            fontSize: "1.05em",
            color: "#000",
            marginBottom: "0.3em",
            textAlign: "center",
          }}
        >
          {dynTr(props.preset.translations, (it) => it.name) || "\u00A0"}
        </Typography>
        <Typography
          component="span"
          sx={{
            fontFamily: '"Montserrat", sans-serif',
            textTransform: "none",
            fontWeight: "normal",
            fontSize: "0.95em",
            color: "#333333",
            textAlign: "center",
          }}
        >
          {props.preset.width !== null && props.preset.height !== null
            ? `${props.preset.width} x ${props.preset.height}`
            : "\u00A0"}
        </Typography>
      </Box>
    </Button>
  );
}

function TypeChoice(props: {
  isFirst: boolean;
  type: SofaType;
  onClick: () => void;
  isPopup?: boolean;
}) {
  const { dynTr } = useContext(Trans);
  const largerThen750 = useMediaQuery("(min-width:750px)");
  return (
    <Button
      className={props.isFirst ? "ree-ug-first-type-button" : undefined}
      sx={{
        /*
         [vB.xs]: {
minWidth: "100px",
},
[vB.lg]: {
minWidth: "150px",
},
*/
        marginRight: props.isPopup ? 0 : "10px",
        width: "100%",
        minWidth: 0,
        padding: 0,
        lineHeight: "1.1em",
        opacity: 1.0,
        flexDirection: "column",
        alignItems: "center",
        border: "1px solid #d9d9d9",
        borderRadius: "8px",
        transition: "all 120ms linear",
        ":hover": {
          backgroundColor: "transparent",
          borderColor: "#bdbdbd",
          boxShadow: "0 4px 10px rgba(0,0,0,0.14)",
        },
      }}
      onClick={props.onClick}
    >
      {props.type.icon && (
        <Box sx={iconWrapperCss(largerThen750)}>
          <Box
            component="img"
            onDragStart={(e) => e.preventDefault()}
            src={`${config.apiUrl}/assets/${props.type.icon}`}
            sx={iconImgCss()}
          />
        </Box>
      )}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          marginTop: "8px",
        }}
      >
        <Typography
          component="span"
          sx={{
            fontFamily: '"Montserrat", sans-serif',
            textTransform: "none",
            fontWeight: "bold",
            fontSize: "1.05em",
            color: "#000",
            marginBottom: "0.3em",
            textAlign: "center",
          }}
        >
          {dynTr(props.type.translations, (it) => it.name) || "\u00A0"}
        </Typography>
      </Box>
    </Button>
  );
}

function UserPresetChoice(props: {
  selected: boolean;
  name: string;
  onClick: () => void;
  onDelete: () => void;
  isPopup?: boolean;
}) {
  const largerThen750 = useMediaQuery("(min-width:750px)");
  return (
    <Box
      sx={{
        display: "inline-block",
        position: "relative",
        marginRight: props.isPopup ? 0 : "15px",
      }}
    >
      <Button
        sx={{
          [vB.xs]: {
            height: "60px",
          },
          [vB.lg]: {
            height: largerThen750 ? "80px" : "60px",
          },
          minWidth: 0,
          padding: 0,
          paddingRight: props.selected ? "15px" : 0,
          lineHeight: "1.1em",
          opacity: 1.0,
          transition: "all 120ms linear",
          ":hover": {
            backgroundColor: "transparent",
            borderColor: "#bdbdbd",
            boxShadow: "0 4px 10px rgba(0,0,0,0.14)",
          },
        }}
        onClick={props.onClick}
      >
        <Box sx={{ display: "flex", alignItems: "center", marginLeft: "10px" }}>
          <Typography
            component="span"
            sx={{
              fontFamily: '"Montserrat", sans-serif',
              textTransform: "none",
              // font: 'normal 100% "Open Sans", sans-serif',
              fontWeight: "bold",
              fontSize: "0.9em",
              color: "#000",
              marginBottom: "0.3em",
            }}
          >
            {props.name || "\u00A0"}
          </Typography>
        </Box>
      </Button>
      {props.selected && (
        <IconButton
          sx={{
            position: "absolute",
            top: "0",
            right: "-15px",
            border: "solid 1px",
            padding: "3px",
          }}
          onClick={props.onDelete}
        >
          <CloseIcon />
        </IconButton>
      )}
    </Box>
  );
}

export default function PresetChooser(props: {
  typesData: TypesData | undefined;
  curType: number;
  activeType?: number;
  curPreset: CurPreset | undefined;
  userTypes: UserTypes;
  onTypeClick: (type: SofaType) => void;
  onPresetClick: (preset: SofaPreset) => void;
  onUserPresetClick: (preset: UserPreset) => void;
  onUserPresetDelete: (preset: UserPreset) => void;
  onTypesButtonClick: () => void;
  showCurrentElements: () => void;
  space: Space;
  layout?: "inline" | "popup";
  showHeader?: boolean;
  showBackButton?: boolean;
}) {
  const { _, dynTr } = useContext(Trans);
  const verticalLg = useMediaQuery(vB.lg);
  const largerThen750 = useMediaQuery("(min-width:750px)");
  const [params] = useSearchParams();
  const curPreset = props.curPreset;
  const [isRedCheckOn, setIsRedCheckOn] = useState<boolean>(true);
  const activeType = props.activeType ?? props.curType;
  const isPopup = props.layout === "popup";
  const showBackButton = props.showBackButton !== false;
  const showHeader = props.showHeader !== false;
  const children = [];
  if (activeType === -1) {
    children.push(
      ...(props.typesData?.data ?? []).map((it, index) => {
        return (
          <TypeChoice
            isFirst={index === 0}
            key={it.id}
            type={it}
            isPopup={isPopup}
            onClick={() => props.onTypeClick(it)}
          />
        );
      })
    );
  } else {
    children.push(
      ...(
        props.typesData?.data.find((it) => it.id === activeType)?.presets ??
        []
      ).map((it, index) => {
        return (
          <PresetChoice
            className={`ree-ug-preset-button-${index}`}
            key={it.id}
            selected={
              curPreset !== undefined &&
              !curPreset.isUserPreset &&
              it.id === curPreset.id
            }
            preset={it}
            isPopup={isPopup}
            onClick={() => props.onPresetClick(it)}
          />
        );
      })
    );
    if (!isPopup) {
      children.push(
        ...(props.userTypes[activeType]?.presets ?? []).map((it) => {
          return (
            <UserPresetChoice
              key={it.id}
              selected={
                curPreset !== undefined &&
                curPreset.isUserPreset &&
                it.id === curPreset.id
              }
              name={it.name}
              isPopup={isPopup}
              onClick={() => props.onUserPresetClick(it)}
              onDelete={() => props.onUserPresetDelete(it)}
            />
          );
        })
      );
    }
  }
  return (
    <Box
      sx={{
        className: "ree-ug-types",
        pointerEvents: "initial",
        position: isPopup ? "relative" : "absolute",
        left: isPopup ? "auto" : 0,
        top: isPopup ? "auto" : 0,
        width: isPopup ? "100%" : undefined,
        "@media (orientation: portrait)": isPopup ? undefined : { right: 0 },
        "@media (orientation: landscape)": isPopup
          ? undefined
          : {
              right:
                activeType === -1
                  ? "0"
                  : { xs: "250px", lg: "350px", xl: "500px" },
            },
        [vB.xs]: isPopup ? { padding: 0 } : { height: "60px" },
        [vB.lg]: isPopup
          ? { padding: 0 }
          : { height: largerThen750 ? "160px" : "60px" },
      }}
    >
      {showHeader && (
        <Box
          sx={{
            [vB.xs]: {
              display: params.get("adminKey") !== null ? "block" : "none",
            },
            [vB.lg]: {
              marginTop: "15px",
              display: largerThen750 ? "block" : "none",
              height: "60px",
            },
            display: "flex",
            alignItems: "center",
          }}
        >
          {largerThen750 && (
            <Box
              component="a"
              href={dynTr(props.space.translations, (it) => it.logo_link)}
              onDragStart={(e) => e.preventDefault()}
            >
              <Box
                component="img"
                onDragStart={(e) => e.preventDefault()}
                sx={{ height: "40px", marginLeft: "25px" }}
                src={
                  props.space.logo
                    ? `${config.apiUrl}/assets/${props.space.logo}`
                    : `imgs/${config.spaceId}_logo.svg`
                }
              />
            </Box>
          )}
          {params.get("adminKey") !== null && (
            <Button
              sx={{ position: "absolute", right: "210px" }}
              onClick={props.showCurrentElements}
              variant="outlined"
            >
              Elements
            </Button>
          )}
          {params.get("adminKey") !== null && (
            <Button
              sx={{ position: "absolute", right: "330px" }}
              onClick={async () => {
                const res = await client.request("SofaAdminSwitchRed", {});
                setIsRedCheckOn(res.is_red_on);
              }}
              variant="outlined"
            >
              {isRedCheckOn ? "Red check on" : "No red for me"}
            </Button>
          )}
          {params.get("adminKey") !== null && (
            <Box
              sx={{
                display: "inline-flex",
                position: "absolute",
                right: "485px",
              }}
            >
              <AdminRender />
            </Box>
          )}
          {params.get("adminKey") !== null && (
            <Box
              sx={{
                display: "inline-flex",
                position: "absolute",
                right: "620px",
              }}
            >
              <AdminPricesSync />
            </Box>
          )}
        </Box>
      )}
      <Box
        sx={{
          display: "flex",
          flexDirection: isPopup ? "column" : "row",
          alignItems: isPopup ? "stretch" : "center",
          width: isPopup ? "100%" : undefined,
        }}
      >
        {activeType === -1 || !showBackButton ? null : verticalLg && largerThen750 ? (
          <Button
            className="ree-ug-back-to-types-button"
            sx={{
              // position: "absolute",
              marginLeft: "20px",
              // bottom: "27px",
              flex: "none",
            }}
            key={-1}
            variant="outlined"
            startIcon={<ArrowBackIosNewIcon />}
            onClick={props.onTypesButtonClick}
          >
            {_("Back")}
          </Button>
        ) : !showBackButton ? null : (
          <IconButton
            className="ree-ug-back-to-types-button"
            key={-1}
            onClick={props.onTypesButtonClick}
            color="primary"
            sx={{
              marginLeft: isPopup ? 0 : "var(--sal)",
              zIndex: 100,
              backgroundColor: "white",
            }}
          >
            <ArrowBackIosNewIcon />
          </IconButton>
        )}
        <Box
          className={activeType === -1 ? "ree-ug-types" : "ree-ug-presets"}
          sx={{
            flex: isPopup ? 1 : undefined,
            minWidth: isPopup ? 0 : undefined,
            marginLeft: isPopup
              ? "0"
              : activeType === -1
              ? verticalLg && largerThen750
                ? "5px"
                : "calc(var(--sal) + 5px)"
              : verticalLg && largerThen750
              ? "20px"
              : "5px",
            // display: "flex",
            [vB.xs]: {
              // alignItems: "center",
              // gap: "10px",
              height: isPopup ? "auto" : "60px",
            },
            [vB.lg]: {
              // alignItems: "center",
              // gap: "25px",
              height: isPopup ? "auto" : largerThen750 ? "80px" : "60px",
            },
            overflowY: "auto",
            overflowX: "hidden",
            whiteSpace: isPopup ? "normal" : "nowrap",
            display: isPopup ? "grid" : undefined,
            gridTemplateColumns: isPopup ? "repeat(4, minmax(0, 1fr))" : undefined,
            "@media (max-width:1200px)": isPopup
              ? { gridTemplateColumns: "repeat(3, minmax(0, 1fr))" }
              : undefined,
            "@media (max-width:900px)": isPopup
              ? { gridTemplateColumns: "repeat(2, minmax(0, 1fr))" }
              : undefined,
            "@media (max-width:600px)": isPopup
              ? { gridTemplateColumns: "repeat(1, minmax(0, 1fr))" }
              : undefined,
            gap: isPopup ? "12px" : undefined,
            alignItems: isPopup ? "flex-start" : undefined,
            width: isPopup ? "100%" : undefined,
            maxWidth: isPopup ? "100%" : undefined,
            boxSizing: isPopup ? "border-box" : undefined,
            padding: isPopup ? "0 0 16px" : undefined,
            margin: isPopup ? "0 0 12px" : undefined,
            "& .MuiButton-root": isPopup
              ? {
                  width: "100%",
                  minHeight: "140px",
                  paddingTop: "12px",
                  paddingBottom: "12px",
                  justifyContent: "center",
                  boxSizing: "border-box",
                }
              : undefined,
          }}
        >
          {children}
        </Box>
      </Box>
    </Box>
  );
}
